/** biome-ignore-all lint/suspicious/noExplicitAny: suppressed */
/** biome-ignore-all lint/suspicious/noAssignInExpressions: suppressed */
"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { APP_SETTINGS } from "@/config/settings.config";
import type { Settings } from "@/config/types";

type Path = string;

type SettingsContextType = {
	getOption: <T = any>(path: Path) => T;
	setOption: <T = any>(path: Path, value: T) => void;
	storeOption: <T = any>(path: Path, value: T) => void;
	settings: Settings;
};

const SettingsContext = createContext<SettingsContextType | undefined>(
	undefined,
);

const LOCAL_STORAGE_PREFIX = "app_settings_";

// Utility to safely access localStorage
const isBrowser = () => typeof window !== "undefined";

function getFromPath(obj: any, path: string): any {
	return path.split(".").reduce((acc, part) => acc?.[part], obj);
}

function setToPath(obj: any, path: string, value: any): Settings {
	const keys = path.split(".");
	const lastKey = keys.pop() as string;
	const lastObj = keys.reduce((acc, key) => (acc[key] ??= {}), obj);
	lastObj[lastKey] = value;
	return { ...obj };
}

function storeLeaf(path: string, value: unknown) {
	if (!isBrowser()) return;
	try {
		localStorage.setItem(
			`${LOCAL_STORAGE_PREFIX}${path}`,
			JSON.stringify(value),
		);
	} catch (err) {
		console.error("LocalStorage write error:", err);
	}
}

function getLeafFromStorage(path: string): any {
	if (!isBrowser()) return undefined;
	try {
		const item = localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${path}`);
		return item ? JSON.parse(item) : undefined;
	} catch (err) {
		console.error("LocalStorage read error:", err);
		return undefined;
	}
}

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [settings, setSettings] = useState<Settings>(
		structuredClone(APP_SETTINGS),
	);

	// Load settings from localStorage after mount
	useEffect(() => {
		if (!isBrowser()) return;

		const init = structuredClone(APP_SETTINGS);
		Object.keys(localStorage)
			.filter((key) => key.startsWith(LOCAL_STORAGE_PREFIX))
			.forEach((key) => {
				const path = key.replace(LOCAL_STORAGE_PREFIX, "");
				const value = getLeafFromStorage(path);
				if (value !== undefined) {
					setToPath(init, path, value);
				}
			});
		setSettings(init);
	}, []); // Empty dependency array to run once on mount

	const getOption = useCallback(
		<T,>(path: string): T => {
			return getFromPath(settings, path) as T;
		},
		[settings],
	);

	const setOption = useCallback(<T,>(path: string, value: T) => {
		setSettings((prev) => setToPath({ ...prev }, path, value));
	}, []);

	const storeOption = useCallback(<T,>(path: string, value: T) => {
		setSettings((prev) => {
			const newSettings = setToPath({ ...prev }, path, value);
			storeLeaf(path, value);
			return newSettings;
		});
	}, []);

	// Memoize context value to prevent unnecessary re-renders
	const contextValue = useMemo(
		() => ({ getOption, setOption, storeOption, settings }),
		[getOption, setOption, storeOption, settings],
	);

	return (
		<SettingsContext.Provider value={contextValue}>
			{children}
		</SettingsContext.Provider>
	);
};

export const useSettings = () => {
	const ctx = useContext(SettingsContext);
	if (!ctx) {
		throw new Error("useSettings must be used within a SettingsProvider");
	}
	return ctx;
};
